import { ReactNode } from 'react'
import { useAuth } from '~/components/Auth'
import ForbiddenGeneric from '~/components/ForbiddenGeneric'
import { useFeatures } from './FeatureFlags'

/**
 * Team Tools capabilities: single powers a superuser grants from the Django
 * admin, so somebody can be given one job without a whole role flag. The
 * server-side registry is `team_tools/capabilities.py`; these names match it.
 *
 * Every check here is "holds one of the old role flags, OR was granted this".
 * Written that way rather than reading one merged list so that nothing changes
 * for a flag holder: each screen still opens for exactly the flags it opened
 * for before, and a grant only ever adds somebody.
 *
 * This decides what is drawn, never what is allowed. Every endpoint checks for
 * itself, so a stale bundle or a typed URL is still refused.
 */
export type Capability =
    | 'view_anticheat'
    | 'view_players'
    | 'view_servers'
    | 'review_cheat_reports'
    | 'ban_players'
    | 'kick_players'
    | 'manage_tournaments'

export type RoleFlag = 'is_staff' | 'is_developer' | 'is_marketing' | 'is_executive'

/** The role lists the server-side gates use, so a check here names the same people. */
export const ROLES: Record<'staff' | 'developer' | 'marketing' | 'marketingOrDeveloper', RoleFlag[]> = {
    staff: ['is_staff', 'is_developer', 'is_marketing', 'is_executive'],
    developer: ['is_developer', 'is_executive'],
    marketing: ['is_marketing', 'is_executive'],
    marketingOrDeveloper: ['is_marketing', 'is_developer', 'is_executive'],
}

/**
 * Whether the viewer holds one of `roles`, or was granted any of `capabilities`.
 *
 * False while the grant list is loading, which only ever hides something from a
 * grant-only account for a moment: a flag holder passes on the flags alone.
 */
export const useAllowed = (roles: readonly RoleFlag[], capabilities: Capability | readonly Capability[]): boolean => {
    const { auth } = useAuth()
    const { data } = useFeatures()
    if (!auth) return false
    if (roles.some((role) => auth[role] === true)) return true
    const wanted = typeof capabilities === 'string' ? [capabilities] : capabilities
    return wanted.some((c) => data?.granted?.includes(c) === true)
}

/** Whether the viewer holds any of the four role flags. Grant-only accounts do not. */
export const useIsRoleStaff = (): boolean => {
    const { auth } = useAuth()
    return !!auth && ROLES.staff.some((role) => auth[role] === true)
}

export const Allowed = ({
    roles,
    capability,
    show403 = false,
    children,
}: {
    roles: readonly RoleFlag[]
    capability: Capability | readonly Capability[]
    show403?: boolean
    children: ReactNode
}) => (useAllowed(roles, capability) ? <>{children}</> : show403 ? <ForbiddenGeneric /> : null)
